import { ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  IconButton,
  Collapse,
  Tooltip,
  Skeleton,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material';
import ApiContext from 'contexts/ApiContext';
import {
  AssignmentInd,
  ExpandLess,
  ExpandMore,
  AccessTime,
  FiberManualRecord,
  BatteryStd,
  GpsOff,
  SignalWifiOff
} from '@mui/icons-material';
import { limitToLast, onChildAdded, query, ref } from 'firebase/database';
import JumioVerificationBadge, { JumioVerificationMap, verificationFor } from 'components/JumioVerificationBadge';
import { TransactionAlert, parseAlertsResponse } from '../TransactionAlertsSection';
import useFirebase from 'hooks/useFirebase';
import usePresence from 'hooks/usePresence';

interface AssignedDriver {
  uid: string;
  name: string;
}

interface Props {
  participants: AssignedDriver[];
  /** Summary tier still in flight — show the real header with a skeleton
   * body instead of hiding the card (there may turn out to be drivers once
   * it resolves, we just don't know yet). */
  loading?: boolean;
  /** Jumio verification per ResourceData id, fetched once by the details page
   * and shared with the step-history cards. Absent entries render no badge. */
  verificationMap?: JumioVerificationMap;
  /** Transaction the roster belongs to — enables the consolidated live trip
   * status (On Route / Delayed / Route Deviation), derived from the same
   * TransactionAlert feed the map card uses. */
  transactionUid?: string;
  teamId?: string;
  teamUid?: string;
  /** Uids that are actually being live-tracked. A tracked driver gets a live
   * trip-status pill (and, for Super Admins, a stop-tracking control) instead
   * of the plain "Assigned" pill. */
  trackedUids?: string[];
}

type LastLogin = {
  // Canonical ResourceData uid resolved server-side. The batch endpoint keys
  // each entry under BOTH the uid and the Mongo _id, so a participant assigned
  // via a RESOURCE field that stored an _id still resolves here — and this uid
  // is the key the mobile app writes presence under (`user-activity/{uid}`),
  // which is what the presence subscription must use.
  uid?: string | null;
  lastLoginAt: string | null;
  lastLogoutAt: string | null;
  lastSeenAt?: number | null;
  battery?: number | null;
  /** Epoch ms the app marked this user logged out (RTDB presence marker); seeds
   * the presence hook so a logged-out driver reads offline on first paint. */
  loggedOutAt?: number | null;
  name?: string;
};

type DriverStatusKind = 'ON_ROUTE' | 'DELAYED' | 'ROUTE_DEVIATION' | 'UNKNOWN';

const STATUS_COPY: Record<DriverStatusKind, { label: string }> = {
  ON_ROUTE: { label: 'On Route' },
  DELAYED: { label: 'Delayed' },
  ROUTE_DEVIATION: { label: 'Route Deviation' },
  UNKNOWN: { label: 'Unknown' }
};

const STATUS_WINDOW_MS = 10 * 60 * 1000;

/** A tidy titled card for the driver hover tooltips: a status dot + heading,
 *  a hairline divider, then the key/value rows. Keeps every tooltip consistent
 *  and reading as a small info card rather than a run of sentences. */
const TooltipCard: React.FC<{ heading: string; dot: string; note?: string; children: ReactNode }> = ({ heading, dot, note, children }) => (
  <Box sx={{ py: 0.5, minWidth: 210 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: dot, flexShrink: 0 }} />
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', fontSize: '0.66rem' }}>
        {heading}
      </Typography>
    </Box>
    <Divider sx={{ borderColor: 'rgba(255,255,255,0.18)', mb: 0.75 }} />
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>{children}</Box>
    {note && (
      <Typography variant="caption" sx={{ display: 'block', mt: 0.9, opacity: 0.6, fontStyle: 'italic', fontSize: '0.62rem' }}>
        {note}
      </Typography>
    )}
  </Box>
);

/** Aligned label → value row used inside {@link TooltipCard}. `sub` renders a
 *  small muted second line under the value (the relative "5m ago" beneath an
 *  absolute date/time). */
const TooltipRow: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
    <Typography variant="caption" sx={{ opacity: 0.7, fontSize: '0.68rem' }}>
      {label}
    </Typography>
    <Box sx={{ textAlign: 'right' }}>
      <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', fontSize: '0.7rem' }}>
        {value}
      </Typography>
      {sub && (
        <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', fontSize: '0.6rem' }}>
          {sub}
        </Typography>
      )}
    </Box>
  </Box>
);

const relativeTime = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const mins = Math.floor((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString();
};

/**
 * Lists the drivers assigned to a transaction via a form RESOURCE field, with
 * each driver's active/inactive status (or "Never logged in"). This is now the
 * single consolidated place for per-driver info: the duplicate roster row that
 * used to sit below the live map (name + trip status + online/offline) has been
 * folded in here.
 *
 * Only resources that resolve as app drivers are shown — getLastLoginBatch
 * returns a (possibly null-login) entry for category:'user' + App-access
 * resources and omits everything else (vehicles, etc.), which is the gate.
 *
 * Battery + last-seen (from `user-activity/{uid}` in Firebase, written by the
 * Flutter app's heartbeat) are merged server-side into this SAME
 * getLastLoginBatch response.
 *
 * Each row shows, in order: name, Jumio badge, a status pill, and a presence
 * indicator. The status pill is the LIVE trip status (On Route / Delayed /
 * Route Deviation) for drivers that are actually being tracked — derived from
 * recent TransactionAlert entries, exactly as the map card's header does — and
 * a neutral "Assigned" pill for drivers who aren't tracked. Super Admins get a
 * stop-tracking control on tracked drivers.
 *
 * The presence pill reads from the LIVE `user-activity` subscription
 * (usePresence), seeded by the REST response so the first paint is correct.
 */
const AssignedDriverPanel: React.FC<Props> = ({
  participants,
  loading = false,
  verificationMap,
  transactionUid,
  teamId,
  teamUid,
  trackedUids = []
}) => {
  const apiContext = useContext(ApiContext);
  const { database, firebaseUid } = useFirebase();
  const [expanded, setExpanded] = useState(true);
  const [lastLoginMap, setLastLoginMap] = useState<Record<string, LastLogin>>({});
  // Key of the last uidsKey this component finished a getLastLoginBatch
  // lookup for. Used (not a plain boolean) so "pending" is true from the
  // very first render a new uidsKey shows up — not just once the effect
  // below has had a chance to run — otherwise there's a one-frame gap
  // where `loading` (parent) has gone false but this hasn't started yet.
  const [resolvedUidsKey, setResolvedUidsKey] = useState<string | null>(null);

  const uidsKey = useMemo(
    () =>
      participants
        .map((p) => p.uid)
        .filter(Boolean)
        .join(','),
    [participants]
  );

  useEffect(() => {
    if (!apiContext || !uidsKey) return;
    let cancelled = false;
    (async () => {
      try {
        const res: any = await (apiContext as any).getLastLoginBatch?.(uidsKey.split(','));
        const data = res?.data ?? {};
        if (!cancelled) setLastLoginMap(data && typeof data === 'object' ? data : {});
      } catch {
        // best-effort — leave the roster empty if the lookup fails
      } finally {
        if (!cancelled) setResolvedUidsKey(uidsKey);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiContext, uidsKey]);

  const rows = useMemo(() => participants.filter((p) => lastLoginMap[p.uid] !== undefined), [participants, lastLoginMap]);

  // Subscribe to presence by the CANONICAL ResourceData uid, not the raw
  // participant key. A transaction's RESOURCE field can persist a non-uid id
  // (e.g. a Mongo _id); `getLastLoginBatch` is id-shape tolerant and resolves
  // the real uid, but `user-activity/{uid}` is only ever written under that
  // canonical uid — so subscribing by the raw key would watch a node the app
  // never writes and the driver would read as permanently Offline.
  const presenceUidFor = useMemo(
    () => (key: string) => lastLoginMap[key]?.uid ?? key,
    [lastLoginMap]
  );
  const rowUids = useMemo(() => rows.map((r) => presenceUidFor(r.uid)), [rows, presenceUidFor]);
  const presence = usePresence(rowUids, lastLoginMap);

  const trackedSet = useMemo(() => new Set(trackedUids), [trackedUids.join(',')]);

  // ---- Live trip status (moved here from the map card's below-map row) ------
  // Only fetched when there is at least one tracked driver in this roster — a
  // roster of purely form-assigned (untracked) drivers has no trip status to
  // show, so we skip the alert round-trip entirely.
  const [statusByUid, setStatusByUid] = useState<Record<string, DriverStatusKind>>({});
  const inFlightRef = useRef(false);
  const hasTrackedRow = useMemo(() => rows.some((r) => trackedSet.has(r.uid)), [rows, trackedSet]);

  const computeStatus = useCallback((alertsForUser: TransactionAlert[]): DriverStatusKind => {
    if (!alertsForUser.length) return 'ON_ROUTE';
    const latest = alertsForUser
      .filter((a) => {
        if (!a.firedAt) return false;
        return Date.now() - new Date(a.firedAt).getTime() < STATUS_WINDOW_MS;
      })
      .sort((a, b) => new Date(b.firedAt!).getTime() - new Date(a.firedAt!).getTime())[0];
    if (!latest) return 'ON_ROUTE';
    if (latest.ruleType === 'GEO_FENCE') return 'ROUTE_DEVIATION';
    if (latest.ruleType === 'STEP_TIMER' || latest.ruleType === 'INTER_STEP_TRACKING') return 'DELAYED';
    return 'ON_ROUTE';
  }, []);

  const refreshStatuses = useCallback(async () => {
    if (!apiContext || !transactionUid || !hasTrackedRow || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res: any = await (apiContext as any).getTransactionAlerts?.(transactionUid);
      const parsed = parseAlertsResponse(res);
      const byUser = new Map<string, TransactionAlert[]>();
      for (const a of parsed.alerts) {
        const uid = a.context?.userId;
        if (!uid) continue;
        const arr = byUser.get(uid) || [];
        arr.push(a);
        byUser.set(uid, arr);
      }
      const next: Record<string, DriverStatusKind> = {};
      for (const uid of trackedSet) next[uid] = computeStatus(byUser.get(uid) || []);
      setStatusByUid(next);
    } catch {
      const next: Record<string, DriverStatusKind> = {};
      for (const uid of trackedSet) next[uid] = 'UNKNOWN';
      setStatusByUid(next);
    } finally {
      inFlightRef.current = false;
    }
  }, [apiContext, transactionUid, hasTrackedRow, trackedSet, computeStatus]);

  useEffect(() => {
    refreshStatuses();
  }, [refreshStatuses]);

  // Refresh trip status when an ALERT_FIRED lands for this transaction.
  useEffect(() => {
    if (!database || !firebaseUid || !hasTrackedRow || (!teamId && !teamUid)) return;
    const candidates = [
      teamId ? `transaction-refresh/team/${teamId}` : null,
      teamUid ? `transaction-refresh/team/${teamUid}` : null,
      teamId ? `transaction-refresh/${teamId}` : null,
      teamUid ? `transaction-refresh/${teamUid}` : null
    ].filter(Boolean) as string[];
    const unsubs = candidates.map((path) => {
      const r = query(ref(database, path), limitToLast(20));
      return onChildAdded(r, (snap) => {
        const v: any = snap.val();
        if (!v || v.action !== 'ALERT_FIRED') return;
        const isThisTxn = v.keyUid === transactionUid || v.transactionUid === transactionUid || v?.meta?.transactionUid === transactionUid;
        if (isThisTxn) refreshStatuses();
      });
    });
    return () => unsubs.forEach((fn) => fn && fn());
  }, [database, firebaseUid, teamId, teamUid, transactionUid, hasTrackedRow, refreshStatuses]);

  // ---- Super Admin stop-tracking (moved here from the map card's row) -------
  const ctxAny: any = apiContext;
  const _saRole = (ctxAny?.user?.role?.name ?? ctxAny?.user?.role?.roleName ?? ctxAny?.user?.role?.roleType ?? '')
    .toString()
    .toLowerCase()
    .trim();
  const isSuperAdmin = _saRole === 'super admin' || _saRole === 'superadmin' || _saRole === 'sys_admin';
  const [stoppingUid, setStoppingUid] = useState<string | null>(null);
  const [confirmStop, setConfirmStop] = useState<{ uid: string; name: string } | null>(null);
  const handleStopTracking = async (driverUid: string) => {
    if (!ctxAny?.stopDriverTracking || !transactionUid || !driverUid) return;
    setStoppingUid(driverUid);
    try {
      const res: any = await ctxAny.stopDriverTracking(transactionUid, driverUid);
      if (res?.success === false) {
        ctxAny?.showToast?.(typeof res?.message === 'string' ? res.message : 'Failed to stop tracking', 'error');
      } else {
        ctxAny?.showToast?.('Live tracking stopped for the driver', 'success');
      }
    } catch (e: any) {
      ctxAny?.showToast?.(e?.message || 'Failed to stop tracking', 'error');
    } finally {
      setStoppingUid(null);
    }
  };

  // True from the moment participants arrive until this component's own
  // last-login batch lookup for that exact set resolves — covers the gap
  // between the parent's `loading` going false and lastLoginMap catching
  // up, where rows would otherwise read as empty for a beat.
  const batchPending = uidsKey !== '' && uidsKey !== resolvedUidsKey;
  const isLoading = loading || batchPending;

  return (
    <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          bgcolor: 'primary.dark',
          color: '#fff',
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentInd sx={{ color: '#fff', fontSize: 18 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#fff' }}>
            Assigned Driver Info
          </Typography>
          {!isLoading && (
            <Box sx={{ px: 0.75, py: 0.1, borderRadius: 1, bgcolor: 'rgba(255,255,255,0.18)' }}>
              <Typography variant="caption" sx={{ color: '#fff', fontWeight: 700 }}>
                {rows.length}
              </Typography>
            </Box>
          )}
        </Box>
        <IconButton
          size="small"
          sx={{ color: '#fff' }}
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {isLoading ? (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 2, py: 1.5, minHeight: 64, alignItems: 'center' }}>
              {[0, 1].map((i) => (
                <Skeleton key={i} variant="rounded" width={200} height={40} sx={{ borderRadius: 1.5 }} />
              ))}
            </Stack>
          ) : rows.length === 0 ? (
            <Box
              sx={{ px: 2, py: 1.5, minHeight: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}
            >
              <Typography variant="body2" color="text.secondary">
                No drivers assigned to this transaction yet.
              </Typography>
            </Box>
          ) : (
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 2, py: 1.5 }}>
              {rows.map((d) => {
                const ll = lastLoginMap[d.uid];
                // Presence is keyed on the canonical uid (see rowUids above),
                // which may differ from the raw participant key d.uid.
                const p = presence[presenceUidFor(d.uid)];
                const displayName = ll?.name || d.name || d.uid.slice(0, 6);
                const loggedIn = !!ll?.lastLoginAt;
                const isOnline = !!p?.online;
                const lastSeenAt = p?.lastSeenAt ?? null;
                const battery = p?.battery ?? null;
                const isTracked = trackedSet.has(d.uid);
                // Tracked drivers have a live trip status; everyone else is just
                // "Assigned". This is surfaced inside the hover tooltip rather
                // than as a visible pill on the chip.
                const tripStatus: DriverStatusKind | null = isTracked ? statusByUid[d.uid] ?? 'ON_ROUTE' : null;
                const tripStatusLabel = tripStatus ? STATUS_COPY[tripStatus].label : 'Assigned';
                // A driver who started a trip (isTracked) but whose device has
                // gone dark is the case a dispatcher must not miss: the trip is
                // still "live" while we've lost eyes on them. Called out as
                // "Connection lost" rather than a calm "Offline". A non-tracked
                // driver being offline just means their app is closed.
                const lostConnection = isTracked && loggedIn && !isOnline;
                const lastSeenAgo = lastSeenAt != null ? relativeTime(new Date(lastSeenAt).toISOString()) : '';

                const detailTooltip = (
                  <TooltipCard heading={isOnline ? 'Online' : 'Offline'} dot={isOnline ? '#66bb6a' : '#9e9e9e'}>
                    <TooltipRow label="Status" value={tripStatusLabel} />
                    <TooltipRow
                      label="Last seen"
                      value={lastSeenAt != null ? new Date(lastSeenAt).toLocaleString() : 'Unknown'}
                      sub={lastSeenAt != null ? relativeTime(new Date(lastSeenAt).toISOString()) : undefined}
                    />
                    <TooltipRow
                      label="Last login"
                      value={loggedIn ? new Date(ll!.lastLoginAt as string).toLocaleString() : 'Never'}
                      sub={loggedIn ? relativeTime(ll!.lastLoginAt) : undefined}
                    />
                  </TooltipCard>
                );

                return (
                  <Box
                    key={d.uid}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 1.5,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.72rem', textTransform: 'uppercase' }}>
                      {displayName}
                    </Typography>
                    <JumioVerificationBadge verification={verificationFor(verificationMap, d.uid)} />

                    {loggedIn ? (
                      lostConnection ? (
                        <Tooltip
                          title={
                            <TooltipCard heading="Offline" dot="#ffb74d">
                              <TooltipRow label="Status" value={tripStatusLabel} />
                              <TooltipRow label="Battery" value={battery != null ? `${battery}%` : 'Unknown'} />
                              <TooltipRow
                                label="Last seen"
                                value={lastSeenAt != null ? new Date(lastSeenAt).toLocaleString() : 'Unknown'}
                                sub={lastSeenAt != null ? lastSeenAgo : undefined}
                              />
                            </TooltipCard>
                          }
                        >
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.375,
                              px: 0.625,
                              py: 0.125,
                              borderRadius: 1,
                              cursor: 'default',
                              bgcolor: 'warning.main',
                              color: '#fff'
                            }}
                          >
                            <SignalWifiOff sx={{ fontSize: 13 }} />
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, letterSpacing: 0.2 }}>
                              Offline{lastSeenAgo ? ` · ${lastSeenAgo}` : ''}
                            </Typography>
                            {battery != null && (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.25 }}>
                                <BatteryStd sx={{ fontSize: 13 }} />
                                <Typography variant="caption" sx={{ fontSize: '0.68rem', fontWeight: 700 }}>
                                  {battery}%
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      ) : (
                        <Tooltip title={detailTooltip}>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'default'
                            }}
                          >
                            <FiberManualRecord
                              sx={{
                                fontSize: 14,
                                color: isOnline ? 'success.main' : 'text.disabled'
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                color: isOnline ? 'success.main' : 'text.secondary'
                              }}
                            >
                              {isOnline ? 'Online' : 'Offline'}
                            </Typography>
                            {battery != null && (
                              <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, ml: 0.5 }}>
                                <BatteryStd sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                                  {battery}%
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      )
                    ) : (
                      <Tooltip title="Has not logged into the app yet">
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.25,
                            color: 'warning.main'
                          }}
                        >
                          <AccessTime sx={{ fontSize: 13 }} />
                          <Typography variant="caption" sx={{ fontSize: '0.68rem' }}>
                            Never logged in
                          </Typography>
                        </Box>
                      </Tooltip>
                    )}

                    {isTracked && isSuperAdmin && (
                      <Tooltip title="Stop live tracking for this driver (Super Admin)">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            sx={{ p: 0.25 }}
                            disabled={stoppingUid === d.uid}
                            onClick={() => setConfirmStop({ uid: d.uid, name: displayName })}
                          >
                            {stoppingUid === d.uid ? <CircularProgress size={13} /> : <GpsOff sx={{ fontSize: 15 }} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Collapse>

      {/* Stop-tracking confirmation (Super Admin) */}
      <Dialog open={!!confirmStop} onClose={() => setConfirmStop(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Stop live tracking?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            Stop live tracking for <strong>{confirmStop?.name}</strong>? Their location will no longer update, and tracking cannot be
            resumed for this driver on this transaction.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmStop(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              const c = confirmStop;
              setConfirmStop(null);
              if (c) handleStopTracking(c.uid);
            }}
          >
            Stop tracking
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AssignedDriverPanel;
